import * as Yup from "yup";
import { IOption, IPagination } from "../../data/AppInterface";
import { TReactSetState, TUseTranslationTfn } from "../../data/AppType";
import {
  GET_BUSINESS_INFORMATION,
  GET_BUSINESS_INFORMATION_VERSIONS,
  GLOBAL_DROP_DOWN,
  SAVE_BUSINESS_INFORMATION,
} from "../../services/Endpoints";
import { IBusinessInformationPayload } from "../../services/business-information/BusinessInformationService";
import {
  DEFAULT_STATUS_CODE_SUCCESS,
  MESSAGE_UNKNOWN_ERROR_OCCURRED,
} from "../../utils/AppConstants";
import {
  handleError,
  prepareMessageFromParams,
  sweetAlertError,
  sweetAlertSuccess,
  toastError,
} from "../../utils/AppFunctions";
import {
  FOOTER_NOTE_MAX_LENGTH,
  GSTIN_REGEX,
  PAN_REGEX,
  SAC_CODE_REGEX,
  ZIP_CODE_REGEX,
} from "../../utils/PurchaseConstants";

interface IBusinessInformationVersion extends IBusinessInformationPayload {
  id: string;
  version_no: number;
  created_date: string;
  state_name: string | null;
  country_name: string | null;
  saved_by: string | null;
}

export interface IBusinessInformationData {
  current: IBusinessInformationVersion | null;
  default_invoice_footer_note: string;
  default_receipt_footer_note: string;
}

export type TBusinessInformationForm = Record<keyof IBusinessInformationPayload, string>;

export interface IStateOption extends IOption<string> {
  state_code: number | null;
}

export interface IVersionChange {
  field_name: string;
  old_value: string | null;
  new_value: string | null;
}

export interface IVersionHistoryItem {
  version_no: number;
  action_date: string;
  action_by: string | null;
  profile_media_url: string | null;
  title: string;
  log: IVersionChange[];
}

const DEFAULT_COUNTRY_NAME = "India";

const EMPTY_FORM: TBusinessInformationForm = {
  company_name: "",
  gstin: "",
  address_line1: "",
  address_line2: "",
  city: "",
  state_id: "",
  country_id: "",
  zip_code: "",
  pan: "",
  email: "",
  phone: "",
  sac_code: "",
  invoice_footer_note: "",
  receipt_footer_note: "",
};

const optionalText = (value: string): string | null => {
  const text = value.trim();
  return text.length ? text : null;
};

const toGstStateCode = (stateCode: number | null): string =>
  stateCode === null ? "" : String(stateCode).padStart(2, "0");

const requiredMessage = (t: TUseTranslationTfn, fieldName: string) =>
  prepareMessageFromParams(t("error_message_required"), [["fieldName", fieldName]]);

const maxLengthMessage = (t: TUseTranslationTfn, fieldName: string, max: number) =>
  prepareMessageFromParams(t("error_message_max_length"), [
    ["fieldName", fieldName],
    ["max", String(max)],
  ]);

const requiredText = (t: TUseTranslationTfn, fieldName: string, max: number) =>
  Yup.string()
    .trim()
    .required(requiredMessage(t, fieldName))
    .max(max, maxLengthMessage(t, fieldName, max));

const toPayload = (values: TBusinessInformationForm): IBusinessInformationPayload => ({
  company_name: values.company_name.trim(),
  gstin: values.gstin.trim().toUpperCase(),
  address_line1: values.address_line1.trim(),
  address_line2: optionalText(values.address_line2),
  city: values.city.trim(),
  state_id: values.state_id,
  country_id: values.country_id,
  zip_code: values.zip_code.trim(),
  pan: optionalText(values.pan)?.toUpperCase() ?? null,
  email: optionalText(values.email),
  phone: optionalText(values.phone),
  sac_code: optionalText(values.sac_code),
  invoice_footer_note: optionalText(values.invoice_footer_note),
  receipt_footer_note: optionalText(values.receipt_footer_note),
});

export const getBusinessInformationInitialValues = (
  data: IBusinessInformationData,
  countries: IOption<string>[],
): TBusinessInformationForm => {
  const current = data.current;
  if (!current) {
    return {
      ...EMPTY_FORM,
      country_id:
        countries.find((country) => country.display_value === DEFAULT_COUNTRY_NAME)
          ?.data_value ?? "",
      invoice_footer_note: data.default_invoice_footer_note,
      receipt_footer_note: data.default_receipt_footer_note,
    };
  }
  return {
    company_name: current.company_name,
    gstin: current.gstin,
    address_line1: current.address_line1,
    address_line2: current.address_line2 ?? "",
    city: current.city,
    state_id: current.state_id,
    country_id: current.country_id,
    zip_code: current.zip_code,
    pan: current.pan ?? "",
    email: current.email ?? "",
    phone: current.phone ?? "",
    sac_code: current.sac_code ?? "",
    invoice_footer_note: current.invoice_footer_note ?? "",
    receipt_footer_note: current.receipt_footer_note ?? "",
  };
};

export const businessInformationValidationSchema = (
  t: TUseTranslationTfn,
  states: IStateOption[],
) =>
  Yup.object().shape({
    company_name: requiredText(t, t("company_name"), 200),
    gstin: Yup.string()
      .trim()
      .required(requiredMessage(t, t("gstin")))
      .uppercase()
      .matches(GSTIN_REGEX, t("gstin_invalid"))
      .test("gstin-state", "", function (value) {
        const state = states.find((item) => item.data_value === this.parent.state_id);
        const code = toGstStateCode(state?.state_code ?? null);
        if (!value || !state || !code || value.toUpperCase().startsWith(code)) {
          return true;
        }
        return this.createError({
          message: prepareMessageFromParams(t("gstin_state_mismatch"), [
            ["code", code],
            ["state", state.display_value],
          ]),
        });
      }),
    address_line1: requiredText(t, t("address_line_1"), 200),
    address_line2: Yup.string().trim().max(200, maxLengthMessage(t, t("address_line_2"), 200)),
    city: requiredText(t, t("city"), 100),
    state_id: Yup.string().required(requiredMessage(t, t("state"))),
    country_id: Yup.string().required(requiredMessage(t, t("country"))),
    zip_code: requiredText(t, t("zip_code"), 10).matches(ZIP_CODE_REGEX, t("zip_code_invalid")),
    pan: Yup.string().trim().uppercase().matches(PAN_REGEX, {
      message: t("pan_invalid"),
      excludeEmptyString: true,
    }),
    email: Yup.string().trim().email(t("email_invalid")).max(150, maxLengthMessage(t, t("email"), 150)),
    phone: Yup.string().trim().matches(/^.{7,20}$/, {
      message: t("phone_invalid"),
      excludeEmptyString: true,
    }),
    sac_code: Yup.string().trim().matches(SAC_CODE_REGEX, {
      message: t("sac_code_invalid"),
      excludeEmptyString: true,
    }),
    invoice_footer_note: Yup.string()
      .trim()
      .max(FOOTER_NOTE_MAX_LENGTH, maxLengthMessage(t, t("invoice_footer_note"), FOOTER_NOTE_MAX_LENGTH)),
    receipt_footer_note: Yup.string()
      .trim()
      .max(FOOTER_NOTE_MAX_LENGTH, maxLengthMessage(t, t("receipt_footer_note"), FOOTER_NOTE_MAX_LENGTH)),
  });

export const fetchBusinessInformation = async (
  setData: TReactSetState<IBusinessInformationData | undefined>,
) => {
  try {
    const response = await GET_BUSINESS_INFORMATION();
    if (response?.code === DEFAULT_STATUS_CODE_SUCCESS && response.data) {
      setData(response.data);
      return;
    }
    sweetAlertError({ message: response?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED });
  } catch (error) {
    handleError(error, "fetchBusinessInformation");
  }
};

export const fetchCountryOptions = async (
  setCountries: TReactSetState<IOption<string>[]>,
) => {
  try {
    const response = await GLOBAL_DROP_DOWN({
      entity: "countries",
      data_value: "id",
      display_value: "name",
    });
    if (response?.code === DEFAULT_STATUS_CODE_SUCCESS) {
      setCountries(response.data ?? []);
      return response.data ?? [];
    }
    toastError(response?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED);
  } catch (error) {
    handleError(error, "fetchCountryOptions");
  }
  return [];
};

export const fetchStateOptions = async (
  countryId: string,
  setStates: TReactSetState<IStateOption[]>,
) => {
  if (!countryId) {
    setStates([]);
    return;
  }
  try {
    const response = await GLOBAL_DROP_DOWN({
      entity: "states",
      data_value: "id",
      display_value: "name",
      filters: [{ field: "country_id", value: countryId, condition: "eq" }],
      additional_field: ["state_code"],
    });
    if (response?.code === DEFAULT_STATUS_CODE_SUCCESS) {
      setStates(response.data ?? []);
      return;
    }
    toastError(response?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED);
  } catch (error) {
    handleError(error, "fetchStateOptions");
  }
};

export const saveBusinessInformation = async (
  values: TBusinessInformationForm,
  setLoading: TReactSetState<boolean>,
): Promise<boolean> => {
  setLoading(true);
  try {
    const response = await SAVE_BUSINESS_INFORMATION(toPayload(values));
    if (response?.code === DEFAULT_STATUS_CODE_SUCCESS) {
      sweetAlertSuccess({ message: response.message });
      return true;
    }
    sweetAlertError({ message: response?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED });
  } catch (error) {
    handleError(error, "saveBusinessInformation");
  } finally {
    setLoading(false);
  }
  return false;
};

export const fetchVersionHistory = async (
  pagination: IPagination,
): Promise<{ items: IVersionHistoryItem[]; pagination: IPagination } | null> => {
  try {
    const response = await GET_BUSINESS_INFORMATION_VERSIONS(pagination);
    if (response?.code === DEFAULT_STATUS_CODE_SUCCESS && response.data) {
      return { items: response.data.item ?? [], pagination: response.data.pagination };
    }
    sweetAlertError({ message: response?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED });
  } catch (error) {
    handleError(error, "fetchVersionHistory");
  }
  return null;
};
